import CustomBreadcrumb from '@/components/custom-breadcrumb/custom-breadcrumb';
import { useBreadcrumbs } from '@/hooks/use-breadcrumbs/use-breadcrumbs';
import { Flex, Select } from 'antd';
import styles from './phone-numbers.module.scss';
import customFilterStyles from '@/components/custom-filters/custom-filters.module.scss';
import { useUser } from '@/hooks/use-user/use-user';
import { useCompaniesList } from '@/hooks/use-company/use-company';
import { useIncomingNumbers } from '@/hooks/use-phone-numbers/use-phone-numbers';
import { IncomingNumbersTable } from '@/components/incoming-numbers-table/incoming-numbers-table';
import { SearchNumbers } from '@/components/search-numbers/search-numbers';
import { Tab } from '@/components/tab/tab';
import { useEffect, useState } from 'react';
import { useCompanyStore } from '@/store/app.store';
import { useTranslation } from 'react-i18next';

export function PhoneNumbers() {
  const { t } = useTranslation(['phoneNumbers', 'settings']);
  const items = useBreadcrumbs();
  const { userData } = useUser();
  const { companies } = useCompaniesList();
  const { companyName, setCompanyName } = useCompanyStore();

  const [selectedCompanyId, setSelectedCompanyId] = useState<number | undefined>();

  useEffect(() => {
    if (companyName && companies) {
      const matched = companies.find((c) => c.name === companyName);
      if (matched) setSelectedCompanyId(matched.id);
      return;
    }
    if (selectedCompanyId) return;
    if (userData?.company?.id) {
      setSelectedCompanyId(userData.company.id);
      return;
    }
    if (userData?.default_company && companies) {
      const matched = companies.find((c) => c.name === userData.default_company);
      if (matched) setSelectedCompanyId(matched.id);
    }
  }, [companyName, companies, userData?.company?.id, userData?.default_company, selectedCompanyId]);

  const handleCompanyChange = (id: number) => {
    setSelectedCompanyId(id);
    const company = companies?.find((c) => c.id === id);
    setCompanyName(company?.name);
  };

  const { incomingNumbers, isPending } = useIncomingNumbers(selectedCompanyId);

  const companyOptions =
    companies?.map((company) => ({
      label: company.name,
      value: company.id,
    })) || [];

  const tabItems = [
    {
      key: 'incoming',
      label: t('phoneNumbers:tabs.incoming'),
      children: <IncomingNumbersTable numbers={incomingNumbers} loading={isPending} />,
    },
    {
      key: 'search',
      label: t('phoneNumbers:tabs.search'),
      children: <SearchNumbers companyId={selectedCompanyId} />,
    },
  ];

  return (
    <Flex vertical className={styles.container}>
      <CustomBreadcrumb items={items} />

      <div>
        {/* One company is nothing to switch to. The effect above selects it either way. */}
        {companyOptions.length !== 1 && (
          <Select
            className={customFilterStyles.select}
            classNames={{
              popup: { root: customFilterStyles.dropdown },
            }}
            placeholder={t('settings:companySelect.placeholder')}
            value={selectedCompanyId}
            onChange={handleCompanyChange}
            options={companyOptions}
            showSearch
            filterOption={(input, option) =>
              (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        )}

        <Tab items={tabItems} className={styles.tabs} />
      </div>
    </Flex>
  );
}
